import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api/client";
import LoadingState from "../components/common/LoadingState";
import PageHeader from "../components/common/PageHeader";

const initialForm = {
  date: "",
  team_details: "",
  objective_of_meeting: "",
  action_point: "",
  responsible_person: "",
  date_due: ""
};

function PMVisitorLogFormPage() {
  const history = useHistory();
  const { uuid } = useParams();
  const isEditing = Boolean(uuid);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (isEditing) {
      fetchLog();
    }
  }, [uuid]);

  const submitLabel = useMemo(() => (isEditing ? "Update log entry" : "Create log entry"), [isEditing]);

  async function fetchLog() {
    setLoading(true);
    setError("");

    try {
      const response = await api.get(`/pm-visitor-logs/${uuid}`);
      setForm({
        date: response.data.date || "",
        team_details: response.data.team_details || "",
        objective_of_meeting: response.data.objective_of_meeting || "",
        action_point: response.data.action_point || "",
        responsible_person: response.data.responsible_person || "",
        date_due: response.data.date_due || ""
      });
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load PM visitor log entry.");
    } finally {
      setLoading(false);
    }
  }

  async function fetchUsers() {
    try {
      const response = await api.get("/users");
      setUsers(response.data || []);
    } catch (err) {
      // Keep form usable even if the user lookup fails.
    }
  }

  function handleChange(event) {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: value
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);

    try {
      if (isEditing) {
        await api.put(`/pm-visitor-logs/${uuid}`, form);
        toast.success("PM engagement log updated");
      } else {
        await api.post("/pm-visitor-logs", form);
        toast.success("PM engagement log created");
      }

      history.push("/pm-visitor-logs");
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to ${isEditing ? "update" : "create"} PM engagement log.`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Row className="justify-content-center">
      <Col xs={12} xl={10}>
        <PageHeader
          title={isEditing ? "Edit PM Engagement Log" : "Add PM Engagement Log"}
          action={
            <Button variant="outline-secondary" onClick={() => history.push("/pm-visitor-logs")}>
              Back to PM engagement log
            </Button>
          }
        />

        {loading ? <LoadingState label="Loading PM engagement log..." /> : null}
        {error ? <Alert variant="danger">{error}</Alert> : null}

        {!loading ? (
          <div className="content-card form-card">
            <form onSubmit={handleSubmit}>
              <Row className="g-3">
                <Col md={6}>
                  <label className="form-label">Date</label>
                  <Form.Control type="date" name="date" value={form.date} onChange={handleChange} required />
                </Col>
                <Col md={6}>
                  <label className="form-label">Date due</label>
                  <Form.Control type="date" name="date_due" value={form.date_due} onChange={handleChange} />
                </Col>
                <Col md={6}>
                  <label className="form-label">Team details</label>
                  <Form.Control as="textarea" rows={3} name="team_details" value={form.team_details} onChange={handleChange} required />
                </Col>
                <Col md={6}>
                  <label className="form-label">Objective of meeting</label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="objective_of_meeting"
                    value={form.objective_of_meeting}
                    onChange={handleChange}
                    required
                  />
                </Col>
                <Col xs={12}>
                  <label className="form-label">Action point</label>
                  <Form.Control as="textarea" rows={4} name="action_point" value={form.action_point} onChange={handleChange} />
                </Col>
                <Col md={6}>
                  <label className="form-label">Responsible person</label>
                  <Form.Select name="responsible_person" value={form.responsible_person} onChange={handleChange}>
                    <option value="">Select user</option>
                    {users.map((user) => (
                      <option key={user.id} value={[user.firstname, user.lastname].filter(Boolean).join(" ") || user.username}>
                        {[user.firstname, user.lastname].filter(Boolean).join(" ") || user.username}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>

              <div className="d-flex gap-2 mt-4">
                <Button type="submit" disabled={saving}>
                  {saving ? "Saving..." : submitLabel}
                </Button>
                <Button variant="outline-secondary" onClick={() => history.push("/pm-visitor-logs")}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        ) : null}
      </Col>
    </Row>
  );
}

export default PMVisitorLogFormPage;
